// src/modules/app-views/products/pages/Document/tabs/AiInfoTab/PricingSection.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { Flex, Descriptions, Collapse, Tag, Typography, theme } from 'antd';
import { DollarOutlined } from '@ant-design/icons';
import translateX from 'utils/translateX';

const { Text } = Typography;

// *******************************************************************************
// HELPERS
// *******************************************************************************
// Safely render value that might be an object {uid, label} or a string
function renderValue(value) {
  if (!value) return null;
  if (typeof value === 'object' && value.label) return value.label;
  if (typeof value === 'object') return JSON.stringify(value);
  return value;
}

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const PricingSection = ({ data, isMobile }) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const { token } = theme.useToken();
  const pr = data?.pricing;

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. LOCAL VARIABLES
  // ─────────────────────────────────────────────────────────────────────────────
  const hasData = pr && (pr.costPrice !== undefined || pr.salePrice !== undefined || pr.isSuggestedPrice !== undefined || pr.suggestedPriceSource || pr.suggestedPriceSourceType || pr.suggestedPriceConfidence);

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. HELPERS
  // ─────────────────────────────────────────────────────────────────────────────
  const formatCurrency = value => {
    if (value === undefined || value === null) return '-';
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. UI
  // ─────────────────────────────────────────────────────────────────────────────
  if (!hasData) return null;

  return (
    <Collapse
      className="collapsed-content-fit"
      collapsible="icon"
      defaultActiveKey={['pricing']}
      style={{ marginBottom: 16 }}
      items={[
        {
          key: 'pricing',
          label: (
            <Flex align="center" gap={8}>
              <DollarOutlined style={{ color: token.colorPrimary }} />
              <span style={{ fontWeight: 600 }}>{translateX('pricing_info')}</span>
            </Flex>
          ),
          showArrow: false,
          children: (
            <Descriptions
              bordered
              size="small"
              column={1}
              layout={isMobile ? 'vertical' : 'horizontal'}
              styles={{ label: { fontWeight: 500 } }}
            >
              {pr.costPrice !== undefined && (
                <Descriptions.Item label={translateX('cost_price')}>
                  {formatCurrency(pr.costPrice)}
                </Descriptions.Item>
              )}
              {pr.salePrice !== undefined && (
                <Descriptions.Item label={translateX('sale_price')}>
                  {formatCurrency(pr.salePrice)}
                </Descriptions.Item>
              )}
              {pr.costPrice > 0 && pr.salePrice > 0 && (
                <Descriptions.Item label={translateX('profit_margin')}>
                  <Tag color={pr.salePrice > pr.costPrice ? 'green' : 'red'}>
                    {(((pr.salePrice - pr.costPrice) / pr.costPrice) * 100).toFixed(1)}%
                  </Tag>
                </Descriptions.Item>
              )}
              {pr.isSuggestedPrice !== undefined && (
                <Descriptions.Item label={translateX('is_suggested_price')}>
                  {pr.isSuggestedPrice ? (
                    <Tag color="orange">{translateX('yes')}</Tag>
                  ) : (
                    <Tag color="green">{translateX('no')}</Tag>
                  )}
                </Descriptions.Item>
              )}
              {pr.suggestedPriceSource && (
                <Descriptions.Item label={translateX('price_source')}>
                  <a href={renderValue(pr.suggestedPriceSource)} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12 }}>
                    {renderValue(pr.suggestedPriceSource)}
                  </a>
                </Descriptions.Item>
              )}
              {pr.suggestedPriceSourceType && (
                <Descriptions.Item label={translateX('source_type')}>
                  {renderValue(pr.suggestedPriceSourceType)}
                </Descriptions.Item>
              )}
              {pr.suggestedPriceConfidence && (
                <Descriptions.Item label={translateX('price_confidence')}>
                  <Tag color={pr.suggestedPriceConfidence === 'high' ? 'green' : pr.suggestedPriceConfidence === 'medium' ? 'orange' : 'red'}>
                    {pr.suggestedPriceConfidence?.toUpperCase()}
                  </Tag>
                </Descriptions.Item>
              )}
              {pr.suggestedPriceDate && (
                <Descriptions.Item label={translateX('price_date')}>
                  {renderValue(pr.suggestedPriceDate)}
                </Descriptions.Item>
              )}
              {pr.suggestedPriceNotes && (
                <Descriptions.Item label={translateX('price_notes')}>
                  <Text type="secondary" style={{ fontSize: 13 }}>
                    {renderValue(pr.suggestedPriceNotes)}
                  </Text>
                </Descriptions.Item>
              )}
            </Descriptions>
          ),
        },
      ]}
    />
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default PricingSection;
